import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import type { ReactElement, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { MenuItem } from '../components/DropdownMenu'
import { useAuthStore } from '../stores/authStore'
import { DesktopNavigation } from './components/navigation/DesktopNavigation'
import { MobileNavigation } from './components/navigation/MobileNavigation'

interface NavbarProps {
  /** Application name shown on the left, links to "/" */
  appName: string
  /** Nav links shown to all users */
  navItems?: MenuItem[]
  /** Extra element rendered in the centre-right area (e.g. NotificationBell) */
  rightSlot?: ReactNode
}

function Navbar({
  appName,
  navItems = [],
  rightSlot,
}: NavbarProps): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [openDropdowns, setOpenDropdowns] = useState<{
    [key: string]: boolean
  }>({})
  const { user, profile, signOut } = useAuthStore()
  const location = useLocation()

  // Automatically close mobile menu on route change
  // biome-ignore lint/correctness/useExhaustiveDependencies: close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  const toggleMenu = (): void => {
    setIsMenuOpen((prev) => !prev)
  }

  const toggleDropdown = (label: string): void => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }))
  }

  const userInitial = profile?.first_name
    ? profile.first_name.charAt(0).toUpperCase()
    : (user?.email?.charAt(0).toUpperCase() ?? 'U')

  const userDisplayName = profile?.first_name
    ? `${profile.first_name} ${profile.last_name ?? ''}`.trim()
    : (user?.email ?? '')

  return (
    <header className="sticky top-0 z-40 w-full border-gray-200/80 border-b bg-white/85 shadow-xs backdrop-blur-md transition-all">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2.5 sm:px-6">
        {/* Left Side: Brand Logo & Desktop Nav */}
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="group flex items-center gap-2.5 focus:outline-none"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-500 font-bold text-base text-white shadow-xs transition-transform group-hover:scale-105">
              {appName.charAt(0)}
            </div>
            <span className="font-bold text-gray-900 text-lg tracking-tight transition-colors group-hover:text-indigo-600">
              {appName}
            </span>
          </Link>

          {/* Desktop Navigation Links */}
          <DesktopNavigation
            items={navItems}
            openDropdowns={openDropdowns}
            toggleDropdown={toggleDropdown}
          />
        </div>

        {/* Right Side: Slots, Auth State, Mobile Menu Button */}
        <div className="flex items-center gap-2.5">
          {user && rightSlot}

          {user ? (
            <div className="hidden sm:flex sm:items-center sm:gap-2">
              <div className="flex items-center gap-2 rounded-xl bg-gray-50 py-1 pr-2.5 pl-1.5 ring-1 ring-gray-200/60">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 font-semibold text-indigo-700 text-xs">
                  {userInitial}
                </div>
                {userDisplayName && (
                  <span className="max-w-[130px] truncate font-medium text-gray-700 text-xs">
                    {userDisplayName}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={signOut}
                title="Sign out"
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-700 text-xs shadow-xs transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                <span>Sign out</span>
              </button>
            </div>
          ) : (
            <Link
              to="/auth/login"
              className="hidden items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-1.5 font-semibold text-sm text-white shadow-xs transition-all hover:bg-indigo-700 active:bg-indigo-800 sm:inline-flex"
            >
              Sign in
            </Link>
          )}

          {/* Mobile hamburger toggle */}
          <button
            type="button"
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
            className="inline-flex items-center justify-center rounded-xl p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:hidden"
          >
            {isMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      <MobileNavigation
        isOpen={isMenuOpen}
        items={[
          ...navItems,
          ...(user
            ? [{ label: 'Sign out', onClick: signOut }]
            : [{ label: 'Sign in', href: '/auth/login' }]),
        ]}
        openDropdowns={openDropdowns}
        toggleDropdown={toggleDropdown}
        onClose={() => setIsMenuOpen(false)}
      />
    </header>
  )
}

export default Navbar
